import { useCallback, useMemo, useRef, useState } from 'react'
import type { Data, Permissions, PuckAction } from '@puckeditor/core'
import {
  BreakpointId,
  DEFAULT_BREAKPOINT,
  isOverrideBreakpoint,
} from './breakpoints'
import { resolveResponsive } from './resolve'
import { diffPropOverrides } from './diff'
import type { ResponsiveData } from './types'

/** Structure is locked outside Default — only field edits allowed. */
const LOCKED_PERMISSIONS: Partial<Permissions> = {
  drag: false,
  duplicate: false,
  delete: false,
  insert: false,
  edit: true,
}

export interface UseResponsivePuck {
  activeBp: BreakpointId
  /** Switch the breakpoint being edited (folds current edits first). */
  setActiveBp: (bp: BreakpointId) => void
  /** `key` for <Puck> — remounts on breakpoint switch / external load. */
  puckKey: string
  /** Resolved (un-pruned) data to feed <Puck> for the active breakpoint. */
  puckData: Data
  /** `permissions` for <Puck> — locked on non-default breakpoints. */
  permissions: Partial<Permissions> | undefined
  /** Pass to <Puck onChange>. */
  onChange: (edited: Data) => void
  /** Pass to <Puck onAction>: tells Undo/Redo onto a pinned view (pinViews) from an edit. */
  onAction: (action: PuckAction) => void
  /** Full ResponsiveData with latest edits folded in (publish / preview). */
  getData: () => ResponsiveData
  /**
   * Replace the source (external load) and open it at `breakpoint` (Default when
   * omitted). Always remounts <Puck>.
   */
  load: (data: ResponsiveData, breakpoint?: BreakpointId) => void
  /**
   * Fold the pending edits into the source without switching breakpoint, so a later
   * <Puck> mount from `puckData` shows them. For when <Puck> is unmounted for a while
   * (viewing another version) and must come back exactly as it was. `getData` is the
   * same before and after. Puck's history restarts on that mount, so pinned views
   * (pinViews) are dropped.
   */
  flush: () => void
  /**
   * Tie whole documents to the Puck views they give on the active
   * breakpoint, and return those views, to put in Puck's history as they
   * are (`history.setHistories`). When Puck's Undo or Redo shows one of them
   * again, the source is that document again. Puck's history holds one
   * breakpoint's view only, so this is what lets one Undo revert an edit that
   * spanned breakpoints (an AI agent request that switched them).
   *
   * Undo and Redo are told apart from edits by the history action itself
   * (`onAction`: a `set` carrying one of these view objects), never by
   * content, so an edit that happens to rebuild an old view stays an edit.
   * A pin lasts as long as its history entry is reachable: later edits keep
   * it (Undo back past them still restores the whole document), an edit made
   * while Puck shows an earlier pinned entry drops the later ones (Puck
   * truncates them), and a breakpoint switch or a load drops them all.
   */
  pinViews: (documents: ResponsiveData[]) => Data[]
}

/** Puck actions that change the document (the rest touch only the UI or zone registry). */
const EDIT_ACTIONS = new Set<string>([
  'insert',
  'duplicate',
  'replace',
  'replaceRoot',
  'reorder',
  'move',
  'remove',
  'setData',
])

const changesDocument = (action: PuckAction) =>
  EDIT_ACTIONS.has(action.type) ||
  (action.type === 'set' &&
    (typeof action.state === 'function' || 'data' in (action.state ?? {})))

/**
 * Owns the responsive editing model on top of Puck:
 * - keeps one source `ResponsiveData` (base + sparse overrides),
 * - feeds Puck the resolved tree for the active breakpoint,
 * - folds `onChange` edits back as deltas (base on Default, `diff` elsewhere),
 * - locks structure permissions outside Default.
 * Reusable across sites — the editor page just wires it to <Puck>.
 */
export function useResponsivePuck(initial: ResponsiveData): UseResponsivePuck {
  const [source, setSourceState] = useState<ResponsiveData>(initial)
  // The latest source, also when a pinned view restored it between renders.
  const sourceRef = useRef<ResponsiveData>(initial)
  const setSource = useCallback((next: ResponsiveData) => {
    sourceRef.current = next
    setSourceState(next)
  }, [])
  const [activeBp, setActiveBpState] = useState<BreakpointId>(DEFAULT_BREAKPOINT)
  const [reloadKey, setReloadKey] = useState(0)
  const editedRef = useRef<Data | null>(null)
  /** Views handed to Puck's history by pinViews, by identity, with their documents. */
  const pinsRef = useRef<{ view: Data; source: ResponsiveData }[]>([])
  /**
   * Set while Puck applies an Undo or Redo onto a pinned view: the onChange
   * that follows in the same dispatch is that view, not an edit. Any later
   * action clears it (Puck skips onChange when the data did not change).
   */
  const restoringRef = useRef(false)
  /**
   * Which pinned entry Puck shows now (index into pinsRef), or null when it
   * shows a later entry. The pinned views are the start of Puck's history
   * (the agent's `history.setHistories`), so only an edit made while one of
   * them is shown cuts entries off, and only the pinned ones after it.
   */
  const shownPinRef = useRef<number | null>(null)

  const fold = useCallback(
    (src: ResponsiveData, bp: BreakpointId, edited: Data | null): ResponsiveData => {
      if (!edited) return src
      if (bp === DEFAULT_BREAKPOINT) {
        // Default edits the base tree directly.
        return {
          ...src,
          root: edited.root,
          content: edited.content,
          ...(edited.zones ? { zones: edited.zones } : {}),
        }
      }
      // Non-default: store only prop deltas vs the base tree.
      if (!isOverrideBreakpoint(bp)) return src
      const base = resolveResponsive(src, DEFAULT_BREAKPOINT, { prune: false })
      const props = diffPropOverrides(base, edited)
      const responsive = { ...(src.responsive ?? {}) }
      if (Object.keys(props).length) {
        responsive[bp] = { props }
      } else {
        delete responsive[bp]
      }
      return { ...src, responsive }
    },
    [],
  )

  const onAction = useCallback(
    (action: PuckAction) => {
      restoringRef.current = false
      const pins = pinsRef.current
      if (!pins.length) return
      // Puck's history Undo/Redo dispatch a `set` with the entry's own state,
      // so a pinned view arrives as the very object pinViews returned: an
      // identity check, whatever the size of the document.
      const shown =
        action.type === 'set' && typeof action.state === 'object'
          ? action.state?.data
          : undefined
      const index = shown ? pins.findIndex(p => p.view === shown) : -1
      if (index >= 0) {
        // Puck shows this view now; the source catches up with it.
        setSource(pins[index].source)
        editedRef.current = null
        restoringRef.current = true
        shownPinRef.current = index
        return
      }
      if (!changesDocument(action)) return
      // An edit records a new entry after the one shown and drops the entries
      // after that: from a pinned entry, the later pins become unreachable.
      // From the last pin (an edit, or a Redo onto the person's next entry)
      // or from a later entry, every pin is still in the history.
      const at = shownPinRef.current
      if (at !== null && at < pins.length - 1)
        pinsRef.current = pins.slice(0, at + 1)
      shownPinRef.current = null
    },
    [setSource],
  )

  const onChange = useCallback((edited: Data) => {
    if (restoringRef.current) {
      restoringRef.current = false
      return
    }
    editedRef.current = edited
  }, [])

  const setActiveBp = useCallback(
    (next: BreakpointId) => {
      if (next === activeBp) return
      setSource(fold(sourceRef.current, activeBp, editedRef.current))
      editedRef.current = null
      pinsRef.current = []
      shownPinRef.current = null
      restoringRef.current = false
      setActiveBpState(next)
    },
    [activeBp, fold, setSource],
  )

  const getData = useCallback(
    (): ResponsiveData => fold(sourceRef.current, activeBp, editedRef.current),
    // `source` keeps the identity changing on loads and switches, as before.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, activeBp, fold],
  )

  const load = useCallback(
    (data: ResponsiveData, breakpoint: BreakpointId = DEFAULT_BREAKPOINT) => {
      setSource(data)
      editedRef.current = null
      pinsRef.current = []
      shownPinRef.current = null
      restoringRef.current = false
      setActiveBpState(breakpoint)
      setReloadKey(k => k + 1)
    },
    [setSource],
  )

  // The edits stay in editedRef: folding is idempotent (Default replaces the base with
  // the edited tree; other breakpoints diff it against a base the fold leaves alone),
  // so getData returns the same tree before the state update lands and after.
  const flush = useCallback(() => {
    // <Puck> remounts from the source with a fresh history: no pinned view is
    // reachable any more.
    pinsRef.current = []
    shownPinRef.current = null
    restoringRef.current = false
    const edited = editedRef.current
    if (!edited) return
    setSource(fold(sourceRef.current, activeBp, edited))
  }, [activeBp, fold, setSource])

  const pinViews = useCallback(
    (documents: ResponsiveData[]): Data[] => {
      const views = documents.map(d =>
        resolveResponsive(d, activeBp, { prune: false }),
      )
      pinsRef.current = views.map((view, i) => ({
        view,
        source: documents[i],
      }))
      // The history is these views, showing the last one.
      shownPinRef.current = views.length ? views.length - 1 : null
      restoringRef.current = false
      return views
    },
    [activeBp],
  )

  const puckData = useMemo(
    () => resolveResponsive(source, activeBp, { prune: false }),
    [source, activeBp],
  )

  // Memoize the returned API so consumers (and their useCallback/useEffect deps)
  // don't churn on every render — only when something actually changed.
  return useMemo(
    () => ({
      activeBp,
      setActiveBp,
      puckKey: `${reloadKey}-${activeBp}`,
      puckData,
      permissions: isOverrideBreakpoint(activeBp) ? LOCKED_PERMISSIONS : undefined,
      onChange,
      onAction,
      getData,
      load,
      flush,
      pinViews,
    }),
    [
      activeBp,
      setActiveBp,
      reloadKey,
      puckData,
      onChange,
      onAction,
      getData,
      load,
      flush,
      pinViews,
    ],
  )
}
